import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Stack,
  Chip,
  Avatar,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  Cloud,
  Security,
  Speed,
  Storage,
  Code,
  Support,
  DashboardOutlined,
  AutoAwesome,
  CheckCircle,
  Rocket,
} from "@mui/icons-material";

export default function Landing() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useSelector((state) => state.user);

  const features = [
    {
      icon: <Cloud sx={{ fontSize: 40 }} />,
      title: "Multi-Environment Support",
      description:
        "Deploy WordPress sites across Sandbox, Staging, and Production environments with seamless migration.",
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Enterprise Security",
      description:
        "Complete Docker isolation for each site with JWT authentication, RBAC, and secure SFTP access.",
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: "Lightning Fast",
      description:
        "Redis caching, optimized Docker containers, and Traefik load balancing for maximum performance.",
    },
    {
      icon: <Storage sx={{ fontSize: 40 }} />,
      title: "Automated Backups",
      description:
        "Scheduled backups with one-click restoration. Download complete site archives anytime.",
    },
    {
      icon: <Code sx={{ fontSize: 40 }} />,
      title: "Developer Friendly",
      description:
        "RESTful API, CLI tools, WebSocket updates, and comprehensive documentation for developers.",
    },
    {
      icon: <Support sx={{ fontSize: 40 }} />,
      title: "24/7 Monitoring",
      description:
        "Real-time analytics, resource monitoring, and instant notifications for your WordPress sites.",
    },
  ];

  const stats = [
    { value: "99.9%", label: "Uptime" },
    { value: "< 100ms", label: "Response Time" },
    { value: "Enterprise", label: "Security" },
    { value: "24/7", label: "Support" },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40,
              }}
            >
              <AutoAwesome />
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              WP Spark
            </Typography>
            <Chip label="v2.0" size="small" color="primary" />
          </Stack>

          <Stack direction="row" spacing={2}>
            {user ? (
              <Button
                variant="contained"
                startIcon={<DashboardOutlined />}
                onClick={() => navigate("/dashboard")}
                sx={{ borderRadius: 2 }}
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/auth?mode=login")}
                  sx={{ borderRadius: 2 }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate("/auth?mode=signup")}
                  sx={{ borderRadius: 2 }}
                >
                  Get Started
                </Button>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05,
          )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          position: "relative",
          overflow: "hidden",
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
        }}
      >
        {/* Background Decoration */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.main,
              0.1,
            )} 0%, transparent 70%)`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.secondary.main,
              0.1,
            )} 0%, transparent 70%)`,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                <Chip
                  icon={<Rocket />}
                  label="Enterprise WordPress Hosting"
                  color="primary"
                  sx={{ alignSelf: "flex-start" }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    color: "text.primary",
                  }}
                >
                  The Future of{" "}
                  <Box
                    component="span"
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    WordPress Hosting
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ fontSize: "1.25rem", lineHeight: 1.6 }}
                >
                  Enterprise-grade WordPress hosting with complete Docker
                  isolation, multi-environment support, and automated
                  operations. Built for developers, loved by teams.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() =>
                      user
                        ? navigate("/dashboard")
                        : navigate("/auth?mode=signup")
                    }
                    startIcon={<Rocket />}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: "1.1rem",
                      borderRadius: 2,
                      boxShadow: 3,
                    }}
                  >
                    {user ? "Go to Dashboard" : "Start Free Trial"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() =>
                      document
                        .getElementById("features")
                        .scrollIntoView({ behavior: "smooth" })
                    }
                    sx={{ py: 1.5, px: 4, fontSize: "1.1rem", borderRadius: 2 }}
                  >
                    Learn More
                  </Button>
                </Stack>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -20,
                    left: -20,
                    right: 20,
                    bottom: 20,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    borderRadius: 4,
                    opacity: 0.1,
                  },
                }}
              >
                <Card
                  elevation={4}
                  sx={{
                    position: "relative",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={2}>
                      {stats.map((stat, index) => (
                        <Grid size={{ xs: 6 }} key={index}>
                          <Card
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              textAlign: "center",
                              p: 2,
                            }}
                          >
                            <Typography
                              variant="h4"
                              fontWeight={700}
                              color="primary"
                            >
                              {stat.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {stat.label}
                            </Typography>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }} id="features">
        <Stack spacing={6}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Chip
              icon={<AutoAwesome />}
              label="Features"
              color="secondary"
              variant="outlined"
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.75rem" },
                fontWeight: 700,
              }}
            >
              Everything You Need
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              maxWidth="md"
              sx={{ fontSize: "1.1rem" }}
            >
              Built with modern technologies and enterprise-grade architecture
              to deliver the best WordPress hosting experience
            </Typography>
          </Stack>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 4,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Stack spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          width: 64,
                          height: 64,
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography variant="h6" fontWeight={600}>
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {feature.description}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* Tech Stack Section */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Chip
                icon={<Code />}
                label="Technology"
                color="primary"
                variant="outlined"
              />
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.75rem" },
                  fontWeight: 700,
                }}
              >
                Built with Modern Stack
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {[
                {
                  name: "Docker",
                  description: "Complete container isolation per site",
                },
                {
                  name: "React + Redux",
                  description: "Modern frontend with state management",
                },
                {
                  name: "Node.js + Express",
                  description: "High-performance REST API",
                },
                {
                  name: "Prisma ORM",
                  description: "Type-safe database access",
                },
                {
                  name: "MySQL",
                  description: "Reliable data persistence",
                },
                {
                  name: "Redis",
                  description: "Caching and job queuing",
                },
                {
                  name: "Traefik",
                  description: "Automatic load balancing",
                },
                {
                  name: "SFTP",
                  description: "Secure file access",
                },
              ].map((tech, index) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      p: 2,
                      textAlign: "center",
                      height: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="h6" fontWeight={600}>
                        {tech.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tech.description}
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }} id="pricing">
        <Stack spacing={6}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Chip label="Pricing" color="secondary" variant="outlined" />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.75rem" },
                fontWeight: 700,
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              maxWidth="md"
              sx={{ fontSize: "1.1rem" }}
            >
              Choose the plan that fits your needs. All plans include core
              features with no hidden fees.
            </Typography>
          </Stack>

          <Grid container spacing={4} justifyContent="center">
            {[
              {
                name: "Starter",
                price: 29,
                description: "Perfect for personal projects",
                features: [
                  "5 WordPress Sites",
                  "50 GB Storage",
                  "Automated Backups",
                  "SSL Certificates",
                  "24/7 Support",
                ],
                highlighted: false,
              },
              {
                name: "Professional",
                price: 79,
                description: "For growing businesses",
                features: [
                  "15 WordPress Sites",
                  "150 GB Storage",
                  "Automated Backups",
                  "SSL Certificates",
                  "24/7 Priority Support",
                  "CDN Integration",
                  "Staging Environment",
                ],
                highlighted: true,
              },
              {
                name: "Enterprise",
                price: 199,
                description: "For large-scale operations",
                features: [
                  "Unlimited Sites",
                  "500 GB Storage",
                  "Automated Backups",
                  "SSL Certificates",
                  "Dedicated Support",
                  "CDN Integration",
                  "Staging Environment",
                  "Custom Solutions",
                ],
                highlighted: false,
              },
            ].map((plan, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  elevation={plan.highlighted ? 8 : 0}
                  sx={{
                    height: "100%",
                    border: plan.highlighted
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    position: "relative",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  {plan.highlighted && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Stack spacing={3} alignItems="center" textAlign="center">
                      <Typography variant="h5" fontWeight={600}>
                        {plan.name}
                      </Typography>
                      <Box>
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          color="primary"
                        >
                          ${plan.price}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          /month
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {plan.description}
                      </Typography>
                      <Divider sx={{ width: "100%" }} />
                      <Stack spacing={1.5} sx={{ width: "100%" }}>
                        {plan.features.map((feature, fIndex) => (
                          <Box
                            key={fIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CheckCircle
                              sx={{
                                color: "success.main",
                                fontSize: 18,
                              }}
                            />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Stack>
                      <Button
                        variant={plan.highlighted ? "contained" : "outlined"}
                        fullWidth
                        size="large"
                        onClick={() =>
                          user
                            ? navigate("/dashboard")
                            : navigate("/auth?mode=signup")
                        }
                        sx={{ mt: 2, borderRadius: 2 }}
                      >
                        {user ? "Go to Dashboard" : "Get Started"}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            All plans include a 14-day free trial. No credit card required.
          </Typography>
        </Stack>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Card
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "white",
            borderRadius: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
            <Stack spacing={3} alignItems="center">
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: "1.75rem", md: "2.5rem" },
                  fontWeight: 700,
                }}
              >
                Ready to Get Started?
              </Typography>
              <Typography
                variant="h6"
                sx={{ opacity: 0.95, maxWidth: "md", fontSize: "1.1rem" }}
              >
                Join thousands of developers and teams using WP Spark to power
                their WordPress hosting infrastructure
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() =>
                    user
                      ? navigate("/dashboard")
                      : navigate("/auth?mode=signup")
                  }
                  startIcon={<Rocket />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontSize: "1.1rem",
                    bgcolor: "white",
                    color: theme.palette.primary.main,
                    borderRadius: 2,
                    "&:hover": {
                      bgcolor: alpha("#fff", 0.9),
                    },
                  }}
                >
                  {user ? "Go to Dashboard" : "Create Free Account"}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontSize: "1.1rem",
                    borderColor: "white",
                    color: "white",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#fff", 0.1),
                    },
                  }}
                  onClick={() =>
                    user ? navigate("/dashboard") : navigate("/auth?mode=login")
                  }
                >
                  {user ? "Dashboard" : "Sign In"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderTop: `1px solid ${theme.palette.divider}`,
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Typography variant="h6" fontWeight={700}>
                    WP Spark
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Enterprise WordPress hosting platform with complete Docker
                  isolation, multi-environment support, and automated
                  operations.
                </Typography>
                <Stack direction="row" spacing={1}>
                  {["React", "Node.js", "Docker", "MySQL"].map((tech) => (
                    <Chip key={tech} label={tech} size="small" />
                  ))}
                </Stack>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Product
                </Typography>
                <Stack spacing={1}>
                  {["Features", "Pricing", "Security", "Documentation"].map(
                    (item) => (
                      <Typography
                        key={item}
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {item}
                      </Typography>
                    ),
                  )}
                </Stack>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Company
                </Typography>
                <Stack spacing={1}>
                  {["About", "Blog", "Careers", "Contact"].map((item) => (
                    <Typography
                      key={item}
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        cursor: "pointer",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 4, md: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Resources
                </Typography>
                <Stack spacing={1}>
                  {["API Docs", "Guides", "Support", "Status"].map((item) => (
                    <Typography
                      key={item}
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        cursor: "pointer",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Legal
                </Typography>
                <Stack spacing={1}>
                  {["Privacy", "Terms", "License", "Compliance"].map((item) => (
                    <Typography
                      key={item}
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        cursor: "pointer",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2026 WP Spark. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                99.9% Uptime Guaranteed
              </Typography>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
